import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  ChainStatsDBScheme,
  ChainStatsStoreDBScheme,
  StoreStatsDBScheme,
} from "@KassaroAB/shared/types/storeStats";
import {
  computeStoreStatsApi,
  getStatsApi,
  getStatsDayApi,
  getStatsWeekApi,
  getStatsMonthApi,
  getStatsProductsApi,
  getStatsGroupsApi,
  getStoreStatsApi,
  getStoreStatsDayApi,
  getStoreStatsWeekApi,
  getStoreStatsMonthApi,
  getStoreStatsProductsApi,
  getStoreStatsGroupsApi,
  refreshChainStatsApi,
  getChainStatsStoresApi,
  type StatsPeriod,
  type StatsOrderBy,
} from "../api/statsApi";
import type { StatsScope } from "../hooks/useStatsScope";

const PAGE_SIZE = 25;

const parseStats = (scope: StatsScope, data: unknown) =>
  scope.type === "chain"
    ? ChainStatsDBScheme.parse(data)
    : StoreStatsDBScheme.parse(data);

export const useComputeStoreStatsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeId: StoreId) => computeStoreStatsApi(storeId),
    onSuccess: (_, storeId) => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "stats"] });
    },
  });
};

export const useRefreshChainStatsMutation = (chainId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => refreshChainStatsApi(chainId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chains", chainId, "stats"] });
    },
  });
};

export const useGetStatsQuery = (
  scope: StatsScope | undefined,
  period: StatsPeriod,
  from: string,
  to: string
) => {
  return useQuery({
    queryKey: [scope?.type === "chain" ? "chains" : "stores", scope?.id, "stats", period, from, to],
    queryFn: () => getStatsApi(scope!, period, from, to),
    enabled: !!scope,
  });
};

export const useGetStoreStatsQuery = (
  storeId: StoreId | undefined,
  period: StatsPeriod,
  from: string,
  to: string
) => {
  return useQuery({
    queryKey: ["stores", storeId, "stats", period, from, to],
    queryFn: () => getStoreStatsApi(storeId!, period, from, to),
    enabled: !!storeId,
  });
};

export const useGetStatsDayQuery = (
  scope: StatsScope | undefined,
  date: string | undefined
) => {
  return useQuery({
    queryKey: [scope?.type === "chain" ? "chains" : "stores", scope?.id, "stats", "day", date],
    queryFn: () => getStatsDayApi(scope!, date!),
    enabled: !!scope && !!date,
    select: (data) => parseStats(scope!, data),
  });
};

export const useGetStoreStatsDayQuery = (
  storeId: StoreId | undefined,
  date: string | undefined
) => {
  return useQuery({
    queryKey: ["stores", storeId, "stats", "day", date],
    queryFn: () => getStoreStatsDayApi(storeId!, date!),
    enabled: !!storeId && !!date,
    select: (data) => StoreStatsDBScheme.parse(data),
  });
};

export const useGetStatsWeekQuery = (
  scope: StatsScope | undefined,
  weekId: string | undefined
) => {
  return useQuery({
    queryKey: [scope?.type === "chain" ? "chains" : "stores", scope?.id, "stats", "week", weekId],
    queryFn: () => getStatsWeekApi(scope!, weekId!),
    enabled: !!scope && !!weekId,
    select: (data) => parseStats(scope!, data),
  });
};

export const useGetStoreStatsWeekQuery = (
  storeId: StoreId | undefined,
  weekId: string | undefined
) => {
  return useQuery({
    queryKey: ["stores", storeId, "stats", "week", weekId],
    queryFn: () => getStoreStatsWeekApi(storeId!, weekId!),
    enabled: !!storeId && !!weekId,
    select: (data) => StoreStatsDBScheme.parse(data),
  });
};

export const useGetStatsMonthQuery = (
  scope: StatsScope | undefined,
  monthId: string | undefined
) => {
  return useQuery({
    queryKey: [scope?.type === "chain" ? "chains" : "stores", scope?.id, "stats", "month", monthId],
    queryFn: () => getStatsMonthApi(scope!, monthId!),
    enabled: !!scope && !!monthId,
    select: (data) => parseStats(scope!, data),
  });
};

export const useGetStoreStatsMonthQuery = (
  storeId: StoreId | undefined,
  monthId: string | undefined
) => {
  return useQuery({
    queryKey: ["stores", storeId, "stats", "month", monthId],
    queryFn: () => getStoreStatsMonthApi(storeId!, monthId!),
    enabled: !!storeId && !!monthId,
    select: (data) => StoreStatsDBScheme.parse(data),
  });
};

export const useGetStatsProductsQuery = (
  scope: StatsScope | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  params: {
    limit?: number;
    cursor?: string;
    orderBy?: StatsOrderBy;
    storeId?: string;
  } = {}
) => {
  return useQuery({
    queryKey: [
      scope?.type === "chain" ? "chains" : "stores",
      scope?.id,
      "stats",
      period,
      periodId,
      "products",
      params,
    ],
    queryFn: () => getStatsProductsApi(scope!, period, periodId!, params),
    enabled: !!scope && !!periodId,
  });
};

export const useGetStoreStatsProductsQuery = (
  storeId: StoreId | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  params: { limit?: number; cursor?: string; orderBy?: StatsOrderBy } = {}
) => {
  return useQuery({
    queryKey: [
      "stores",
      storeId,
      "stats",
      period,
      periodId,
      "products",
      params,
    ],
    queryFn: () =>
      getStoreStatsProductsApi(storeId!, period, periodId!, params),
    enabled: !!storeId && !!periodId,
  });
};

export const useGetStatsGroupsQuery = (
  scope: StatsScope | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  params: {
    limit?: number;
    cursor?: string;
    orderBy?: StatsOrderBy;
    storeId?: string;
  } = {}
) => {
  return useQuery({
    queryKey: [
      scope?.type === "chain" ? "chains" : "stores",
      scope?.id,
      "stats",
      period,
      periodId,
      "groups",
      params,
    ],
    queryFn: () => getStatsGroupsApi(scope!, period, periodId!, params),
    enabled: !!scope && !!periodId,
  });
};

export const useGetStoreStatsGroupsQuery = (
  storeId: StoreId | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  params: { limit?: number; cursor?: string; orderBy?: StatsOrderBy } = {}
) => {
  return useQuery({
    queryKey: ["stores", storeId, "stats", period, periodId, "groups", params],
    queryFn: () => getStoreStatsGroupsApi(storeId!, period, periodId!, params),
    enabled: !!storeId && !!periodId,
  });
};

export const useGetStatsProductsInfiniteQuery = (
  scope: StatsScope | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  orderBy: StatsOrderBy = "quantity",
  storeId?: string
) => {
  return useInfiniteQuery({
    queryKey: [
      scope?.type === "chain" ? "chains" : "stores",
      scope?.id,
      "stats",
      period,
      periodId,
      "products",
      "infinite",
      orderBy,
      storeId,
    ],
    queryFn: ({ pageParam }) =>
      getStatsProductsApi(scope!, period, periodId!, {
        limit: PAGE_SIZE,
        cursor: pageParam,
        orderBy,
        storeId,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length === PAGE_SIZE
        ? (lastPage[lastPage.length - 1] as { productId: string }).productId
        : undefined,
    enabled: !!scope && !!periodId,
  });
};

export const useGetStoreStatsProductsInfiniteQuery = (
  storeId: StoreId | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  orderBy: StatsOrderBy = "quantity"
) => {
  return useGetStatsProductsInfiniteQuery(
    storeId ? { type: "store", id: storeId } : undefined,
    period,
    periodId,
    orderBy
  );
};

export const useGetStatsGroupsInfiniteQuery = (
  scope: StatsScope | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  orderBy: StatsOrderBy = "quantity",
  storeId?: string
) => {
  return useInfiniteQuery({
    queryKey: [
      scope?.type === "chain" ? "chains" : "stores",
      scope?.id,
      "stats",
      period,
      periodId,
      "groups",
      "infinite",
      orderBy,
      storeId,
    ],
    queryFn: ({ pageParam }) =>
      getStatsGroupsApi(scope!, period, periodId!, {
        limit: PAGE_SIZE,
        cursor: pageParam,
        orderBy,
        storeId,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length === PAGE_SIZE
        ? (lastPage[lastPage.length - 1] as { groupId: string }).groupId
        : undefined,
    enabled: !!scope && !!periodId,
  });
};

export const useGetChainStatsStoresQuery = (
  chainId: string | undefined,
  period: StatsPeriod,
  periodId: string | undefined
) => {
  return useQuery({
    queryKey: ["chains", chainId, "stats", period, periodId, "stores"],
    queryFn: async () => {
      const stores = await getChainStatsStoresApi(chainId!, period, periodId!);
      return stores.map((store) => ChainStatsStoreDBScheme.parse(store));
    },
    enabled: !!chainId && !!periodId,
  });
};

export const useGetStoreStatsGroupsInfiniteQuery = (
  storeId: StoreId | undefined,
  period: StatsPeriod,
  periodId: string | undefined,
  orderBy: StatsOrderBy = "quantity"
) => {
  return useGetStatsGroupsInfiniteQuery(
    storeId ? { type: "store", id: storeId } : undefined,
    period,
    periodId,
    orderBy
  );
};
